import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseVADOptions {
  ws: WebSocket | null;
  /** AnalyserNode from useAudioCapture's AudioContext. Pass null when not capturing. */
  analyserNode: AnalyserNode | null;
}

export interface UseVADReturn {
  isSpeaking: boolean;
  pauseVAD: () => void;
  resumeVAD: () => void;
}

/**
 * Energy threshold (0–255 scale matching getByteFrequencyData output).
 * Frames with average energy above this value count toward speech detection.
 */
const ENERGY_THRESHOLD = 20;

/**
 * Number of consecutive above-threshold frames required to declare speech start.
 * At ~60 fps rAF, 4 frames ≈ 67 ms lead-in.
 */
const SPEECH_START_FRAMES = 4;

/**
 * Number of consecutive below-threshold frames required to declare speech end.
 * At ~60 fps rAF, 72 frames ≈ 1 200 ms silence before we declare end-of-utterance.
 * Balances sentence boundary detection with low latency for non-blocking pipeline.
 */
const SPEECH_END_FRAMES = 72;

/**
 * useVAD — energy-based Voice Activity Detection.
 *
 * Analyses audio energy from an AnalyserNode using requestAnimationFrame.
 * Reports vad:start and vad:end to the backend via WebSocket when speech
 * transitions are detected. Client DOES NOT make pipeline decisions — it
 * reports only (ARCH-009). VAD can be paused externally during TTS playback
 * to prevent the device's own speaker output from triggering false positives.
 */
export function useVAD({ ws, analyserNode }: UseVADOptions): UseVADReturn {
  const [isSpeaking, setIsSpeaking] = useState(false);

  /** When true, VAD analysis is suspended (e.g. during TTS playback). */
  const pausedRef = useRef(false);
  const isSpeakingRef = useRef(false);
  const aboveThresholdCountRef = useRef(0);
  const belowThresholdCountRef = useRef(0);
  const rafIdRef = useRef<number | null>(null);

  // Keep a stable ref to ws so the rAF loop always sees the current socket.
  const wsRef = useRef<WebSocket | null>(ws);
  useEffect(() => {
    wsRef.current = ws;
  }, [ws]);

  const sendControl = useCallback((type: string) => {
    const socket = wsRef.current;
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ type }));
    }
  }, []);

  const pauseVAD = useCallback(() => {
    pausedRef.current = true;
  }, []);

  const resumeVAD = useCallback(() => {
    pausedRef.current = false;
    // Reset hysteresis counters so we don't carry stale state from before the pause.
    aboveThresholdCountRef.current = 0;
    belowThresholdCountRef.current = 0;
  }, []);

  useEffect(() => {
    if (!analyserNode) {
      return;
    }

    const dataArray = new Uint8Array(analyserNode.frequencyBinCount);

    function tick() {
      rafIdRef.current = requestAnimationFrame(tick);

      if (pausedRef.current) {
        // While paused, reset hysteresis but do not send events.
        aboveThresholdCountRef.current = 0;
        belowThresholdCountRef.current = 0;
        return;
      }

      analyserNode!.getByteFrequencyData(dataArray);

      // Compute mean energy across all frequency bins.
      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        sum += dataArray[i];
      }
      const energy = sum / dataArray.length;

      if (energy >= ENERGY_THRESHOLD) {
        aboveThresholdCountRef.current++;
        belowThresholdCountRef.current = 0;

        if (
          !isSpeakingRef.current &&
          aboveThresholdCountRef.current >= SPEECH_START_FRAMES
        ) {
          isSpeakingRef.current = true;
          setIsSpeaking(true);
          sendControl('vad:start');
        }
      } else {
        belowThresholdCountRef.current++;
        aboveThresholdCountRef.current = 0;

        if (
          isSpeakingRef.current &&
          belowThresholdCountRef.current >= SPEECH_END_FRAMES
        ) {
          isSpeakingRef.current = false;
          setIsSpeaking(false);
          sendControl('vad:end');
        }
      }
    }

    rafIdRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      // Reset all state when the analyser changes or unmounts.
      isSpeakingRef.current = false;
      setIsSpeaking(false);
      aboveThresholdCountRef.current = 0;
      belowThresholdCountRef.current = 0;
    };
  }, [analyserNode, sendControl]);

  return { isSpeaking, pauseVAD, resumeVAD };
}
